import React, { useState, useMemo } from 'react';
import { useHelp } from '../context/HelpContext';
import { helpArticles, helpCategories } from '../data/helpContent';

function highlight(text, q) {
  if (!q) return text;
  const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return text.split(re).map((p, i) =>
    i % 2 === 1
      ? <mark key={i} style={{ background: 'var(--warning-light, var(--c-gold-soft))', padding: '0 2px' }}>{p}</mark>
      : p
  );
}

function renderBody(content, q) {
  return content.split('\n').map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} style={{ height: 6 }} />;
    if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      return <h4 key={i} style={{ marginTop: 14, marginBottom: 6, fontSize: 14, fontWeight: 700 }}>
        {highlight(trimmed.slice(2, -2), q)}
      </h4>;
    }
    if (/^\d+\.\s/.test(trimmed)) {
      return <div key={i} style={{ paddingRight: 16, fontSize: 13, lineHeight: 1.7 }}>
        {highlight(trimmed, q)}
      </div>;
    }
    if (trimmed.startsWith('- ')) {
      return <div key={i} style={{ paddingRight: 16, fontSize: 13, lineHeight: 1.7 }}>
        • {highlight(trimmed.slice(2), q)}
      </div>;
    }
    // Bold inline **text**
    const parts = trimmed.split(/\*\*([^*]+)\*\*/g);
    return <p key={i} style={{ margin: '4px 0', fontSize: 13, lineHeight: 1.7 }}>
      {parts.map((p, j) => j % 2 === 1
        ? <strong key={j}>{highlight(p, q)}</strong>
        : highlight(p, q))}
    </p>;
  });
}

export default function Help() {
  const { openTour } = useHelp();
  const [q, setQ] = useState('');
  const [activeCat, setActiveCat] = useState(null);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = helpArticles;
    if (activeCat) list = list.filter(a => a.category === activeCat);
    if (!term) return list;
    return list.filter(a => {
      const haystack = [
        a.title, a.category, a.content,
        ...(a.keywords || []),
      ].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [q, activeCat]);

  return (
    <div data-tour="help-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ marginBottom: 4 }}>📚 مركز المساعدة</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            دليل شامل قابل للبحث لجميع ميزات المنصّة
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => openTour('welcome')}>
          🎬 إعادة تشغيل الجولة التفاعلية الكاملة
        </button>
      </div>

      <div className="help-search" data-tour="help-search">
        <input
          autoFocus
          className="form-input"
          placeholder="🔍 ابحث في المساعدة... (مثلاً: kanban, تفريغ, مسار)"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
      </div>

      <div className="help-cats" data-tour="help-cats">
        <button
          className={`help-cat ${activeCat === null ? 'active' : ''}`}
          onClick={() => setActiveCat(null)}
        >
          الكل ({helpArticles.length})
        </button>
        {helpCategories.map(c => {
          const count = helpArticles.filter(a => a.category === c).length;
          return (
            <button
              key={c}
              className={`help-cat ${activeCat === c ? 'active' : ''}`}
              onClick={() => setActiveCat(c)}
            >
              {c} ({count})
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty-icon">🔎</div>
            لم يُعثر على نتائج لـ "{q}"
          </div>
        </div>
      ) : (
        <div className="help-articles">
          {filtered.map(a => (
            <article key={a.id} className="help-article">
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                {a.category}
              </div>
              <h3 className="help-article-title">{highlight(a.title, q)}</h3>
              <div className="help-article-body">
                {renderBody(a.content, q)}
              </div>
            </article>
          ))}
        </div>
      )}

      <div style={{ marginTop: 30, padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
        لم تجد ما تبحث عنه؟ تواصل مع المدير أو افتح issue على GitHub.
      </div>
    </div>
  );
}
